import { useState } from 'react';

import { Group, Modal, Stack, Title } from '@mantine/core';
import { useForm } from '@mantine/form';

import { Trans, useTranslation } from 'react-i18next';

import { Button } from '../../../../../shared/components/UI/Button/Button';
import { DateInput } from '../../../../../shared/components/UI/DateInput/DateInput';
import { TextInput } from '../../../../../shared/components/UI/TextInput/TextInput';

import type { FileWithPathAndType } from '../../../../../shared/api/magellan/bidResponses/dto';
import { WorkSpaceUsersSelect } from '../../../../../shared/components/WorkSpaceUsersSelect';
import { BidResponseDocumentType } from '../../../../../shared/entities/BidResponseDocument';
import type { BidResponseDocument } from '../../../../../shared/entities/BidResponseDocument';
import {
  BlueHighlight,
  DocumentInput,
} from '../../../../documents/components/DocumentInput';
import DocumentItem from '../../../../documents/components/DocumentItem';
import { useCreateBidResponseMutation } from '../hooks/bidResponse.hook';

type CreateBidResponseModalProps = {
  opened: boolean;
  onClose: () => void;
};

type FormValues = {
  title: string;
  responseDeadline: Date | null;
  ownerId: string | null;
};

export function CreateBidResponseModal({
  opened,
  onClose,
}: CreateBidResponseModalProps) {
  const { t } = useTranslation(['bidResponse', 'documents', 'common']);
  const { mutate: createBidResponse } = useCreateBidResponseMutation();

  const [bidResponseFiles, setBidResponseFiles] = useState<
    FileWithPathAndType<BidResponseDocumentType>[]
  >([]);

  const form = useForm<FormValues>({
    initialValues: {
      title: '',
      responseDeadline: null,
      ownerId: null,
    },
    validate: {
      title: (value: string) =>
        value.length > 0
          ? null
          : t('vault.modals.createBidResponse.titleInput.error'),
      responseDeadline: (value: Date | null) =>
        !value ? t('vault.input.responseDeadline.error') : null,
      ownerId: (value: string | null) =>
        !value ? t('vault.input.owner.error') : null,
    },
  });

  const handleDrop = (
    newFiles: FileWithPathAndType<BidResponseDocumentType>[],
  ) => {
    setBidResponseFiles(prev => [...prev, ...newFiles]);
  };

  const handleTagUpdate = (
    fileIndex: number,
    type: BidResponseDocumentType,
  ) => {
    setBidResponseFiles(prev => {
      const updatedFiles = [...prev];
      updatedFiles[fileIndex].type = type;
      return updatedFiles;
    });
  };

  const handleClose = () => {
    form.reset();
    setBidResponseFiles([]);
    onClose();
  };

  const handleSubmit = (values: FormValues) => {
    const { responseDeadline, ownerId, title } = values;
    if (!responseDeadline || !ownerId || !title) return;

    createBidResponse({
      title,
      responseDeadline,
      ownerId: Number(ownerId),
      bidResponseFiles,
    });

    handleClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      withCloseButton={false}
      radius="12px"
      size="lg"
      styles={{ body: { padding: 0 } }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing="05" p="05">
          <Title order={4}>{t('vault.modals.createBidResponse.title')}</Title>
          <TextInput
            label={t('vault.modals.createBidResponse.titleInput.label')}
            required
            placeholder={t(
              'vault.modals.createBidResponse.titleInput.placeholder',
            )}
            {...form.getInputProps('title')}
          />
          <DateInput
            label={t('vault.input.responseDeadline.label')}
            placeholder={t('vault.input.responseDeadline.placeholder')}
            required
            {...form.getInputProps('responseDeadline')}
          />
          <WorkSpaceUsersSelect
            required
            user={null}
            label={t('vault.input.owner.label')}
            placeholder={t('vault.input.owner.placeholder')}
            {...form.getInputProps('ownerId')}
          />

          <DocumentInput
            label={t('vault.bidResponseTable.dropZoneInput.label')}
            onDrop={handleDrop}
            dropzoneCaption={
              <Trans
                ns="bidResponse"
                i18nKey="vault.input.dropzoneCaption"
                components={{ highlight: <BlueHighlight /> }}
              />
            }
            documentsPreviewComponent={
              <DocumentsPreviewComponent
                files={bidResponseFiles}
                onChange={handleTagUpdate}
              />
            }
          />

          <Group noWrap spacing="02" position="right">
            <Button variant="subtle" onClick={handleClose}>
              {t('common:cancel')}
            </Button>
            <Button type="submit">
              {t('vault.modals.createBidResponse.submitButton')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function DocumentsPreviewComponent({
  files,
  onChange,
}: {
  files: FileWithPathAndType<BidResponseDocumentType>[];
  onChange: (fileIndex: number, type: BidResponseDocumentType) => void;
}) {
  const documents = files.map(({ file, type }, index) => ({
    id: index,
    prettyName: file.name,
    originalName: file.name,
    type: type || BidResponseDocumentType.UNKNOWN,
    S3FileURL: '',
    S3PreviewFileURL: '',
    originalFilePath: '',
    bidResponseId: 0,
  })) satisfies BidResponseDocument[];

  return documents.map(document => (
    <DocumentItem key={document.id} document={document}>
      <DocumentItem.Icon />
      <DocumentItem.Name />
      <DocumentItem.TypeSelect.BidResponseDocument
        onChange={bidResponseDocumentType =>
          onChange(document.id, bidResponseDocumentType)
        }
      />
    </DocumentItem>
  ));
}
